import { Controller } from '@hotwired/stimulus';

// #2038 Step 1: 給与項目の copy/no-copy AI 判定の生成とポーリング表示(管理者向け)。
// ai_comment_controller と同型。
export default class extends Controller {
  static targets = ['body', 'footer', 'button', 'overlayButton'];
  static values = {
    showUrl: String,
    createUrl: String,
    pending: Boolean,
  };

  connect() {
    if (this.pendingValue) {
      this.disableButton();
      this.startPolling();
    }
  }

  create(event) {
    event.preventDefault();
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;

    fetch(this.createUrlValue, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken },
    })
      .then((response) => response.json())
      .then(() => {
        this.showLoading();
        this.startPolling();
      });
  }

  showLoading() {
    this.bodyTarget.innerHTML = `
      <div class="text-center py-3">
        <div class="spinner-border spinner-border-sm text-info me-2" role="status"></div>
        <span>判定を生成中...</span>
      </div>`;
    this.disableButton();
  }

  disableButton() {
    if (this.hasButtonTarget) this.buttonTarget.disabled = true;
  }

  enableButton() {
    if (this.hasButtonTarget) this.buttonTarget.disabled = false;
  }

  startPolling() {
    this.pollTimer = setInterval(() => {
      fetch(this.showUrlValue, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === 'completed') {
            this.stopPolling();
            this.renderItems(data.items || []);
            if (this.overlayRequested) {
              this.overlayRequested = false;
              this.applyOverlay(data.items || []);
            }
          } else if (data.status === 'failed') {
            this.stopPolling();
            this.renderFailed(data);
          }
        });
    }, 3000);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  disconnect() {
    this.stopPolling();
  }

  renderItems(items) {
    if (!items.length) {
      this.bodyTarget.innerHTML = '<p class="text-muted">判定結果がありません。</p>';
      this.enableButton();
      return;
    }
    const rows = items
      .map((item) => {
        const badge = item.copy
          ? '<span class="badge text-bg-success">🔄 コピー</span>'
          : '<span class="badge text-bg-secondary">🚫 コピーしない</span>';
        const reason = item.reason
          ? `<small class="text-muted">${this.escapeHtml(item.reason)}</small>`
          : '';
        return `
          <li class="list-group-item d-flex justify-content-between align-items-start">
            <div>
              <div class="fw-bold">${this.escapeHtml(item.name || '')}</div>
              ${reason}
            </div>
            ${badge}
          </li>`;
      })
      .join('');
    this.bodyTarget.innerHTML = `<ul class="list-group list-group-flush">${rows}</ul>`;
    this.enableButton();
  }

  renderFailed(data) {
    const msg = this.escapeHtml(data.error || '不明なエラー');
    this.bodyTarget.innerHTML = `<p class="text-danger">判定の生成に失敗しました: ${msg}</p>`;
    this.enableButton();
  }

  // #2038 Step 2: 「コピーしない」判定の項目名セル(th[data-copy-key])に
  // オーバーレイを重ねて、表のどの行が引き継がれないかを直接確認できるようにする。
  // 判定が未生成なら生成(ポーリング)してから適用する。ボタンで表示/非表示をトグル。
  toggleOverlay(event) {
    event.preventDefault();

    if (this.overlayActive()) {
      this.clearOverlay();
      return;
    }

    fetch(this.showUrlValue, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 'completed') {
          this.applyOverlay(data.items || []);
        } else if (data.status === 'failed') {
          this.renderFailed(data);
        } else {
          this.overlayRequested = true;
          this.create(event);
        }
      });
  }

  applyOverlay(items) {
    items
      .filter((item) => item.copy === false && item.key)
      .forEach((item) => {
        document
          .querySelectorAll(`[data-copy-key="${CSS.escape(item.key)}"]`)
          .forEach((cell) => {
            if (cell.querySelector('.copy-overlay-badge')) return;

            cell.classList.add('copy-overlay-host');
            const badge = document.createElement('span');
            badge.className = 'copy-overlay-badge';
            badge.textContent = 'コピーしない';
            cell.appendChild(badge);
          });
      });
    this.updateOverlayButton();
  }

  clearOverlay() {
    document.querySelectorAll('.copy-overlay-badge').forEach((badge) => badge.remove());
    document
      .querySelectorAll('.copy-overlay-host')
      .forEach((cell) => cell.classList.remove('copy-overlay-host'));
    this.updateOverlayButton();
  }

  overlayActive() {
    return document.querySelector('.copy-overlay-badge') !== null;
  }

  updateOverlayButton() {
    if (!this.hasOverlayButtonTarget) return;

    this.overlayButtonTarget.textContent = this.overlayActive()
      ? 'コピーしない表示を消す'
      : 'コピーしない項目を表に表示';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
